import type { IWedding_RSVP } from '@bridebook/models/source/models/Weddings.types';
import { deleteField, Timestamp } from 'firebase/firestore';
import debounce from 'lodash/debounce';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { Flex, Text } from '@bridebook/aura';
import { RsvpText } from '@bridebook/web/components/wedding-website/rsvp/rsvp-list/partials/rsvp-text';
import { RsvpTitle } from '@bridebook/web/components/wedding-website/rsvp/rsvp-list/partials/rsvp-title';
import DateInputNative from '@bridebook/web/components/wedding-website/shared/date-input-native/date-input-native';
import { interactedWithRsvpsDeadlineAnalytics } from '@bridebook/web/lib/rsvp/analytics/actions';
import { useUpdateWeddingRsvp } from '@bridebook/web/lib/rsvp/query/hooks/wedding/use-update-wedding-rsvp';
import { useWeddingRsvp } from '@bridebook/web/lib/rsvp/query/hooks/wedding/use-wedding-rsvp';
import { useDispatch } from '@bridebook/web/lib/utils';
import { useEditorSyncContext } from '@bridebook/web/components/wedding-website/main/desktop/editor-sync-context';
import { Controller, useFormContext } from 'react-hook-form';
import { IRsvpDeadlineFormFields } from '@bridebook/web/lib/wedding-website/hooks/use-rsvp-deadline-form';

export const RsvpDeadline = () => {
  const form = useFormContext<IRsvpDeadlineFormFields>();
  const { t } = useTranslation('weddingWebsite');
  const dispatch = useDispatch();
  const { data: weddingRsvp } = useWeddingRsvp();
  const { mutateAsync: updateWeddingRsvpAsync } = useUpdateWeddingRsvp();
  const { triggerPreviewSync } = useEditorSyncContext();
  const { control, handleSubmit } = form;

  const onSubmit = useCallback(
    async (data: { deadline?: string }) => {
      const dateString = data.deadline?.trim() || '';
      const dateStringInUTC = dateString ? `${dateString}T00:00:00Z` : '';

      const action: 'added' | 'edited' | 'deleted' | null = dateString
        ? weddingRsvp?.deadline
          ? 'edited'
          : 'added'
        : weddingRsvp?.deadline
          ? 'deleted'
          : null;

      if (!action) return;

      const deadline =
        action === 'deleted' ? deleteField() : Timestamp.fromDate(new Date(dateStringInUTC));

      await updateWeddingRsvpAsync({ ...weddingRsvp, deadline } as Partial<IWedding_RSVP>);
      dispatch(
        interactedWithRsvpsDeadlineAnalytics({
          action,
          rsvpDeadline: dateStringInUTC,
        }),
      );
    },
    [dispatch, updateWeddingRsvpAsync, weddingRsvp],
  );

  // Debounced submit prevents a double-write when the clear button is clicked while
  // the input is focused. Browsers fire blur (mousedown steals focus) before click,
  // so without debouncing, onBlur submits the old value and then onClear submits the
  // empty value — two Firestore writes instead of one. The short delay lets onClear
  // cancel the pending onBlur submission so only the clear goes through.
  const debouncedSubmit = useMemo(
    () => debounce(() => handleSubmit(onSubmit)(), 50),
    [handleSubmit, onSubmit],
  );
  useEffect(() => () => debouncedSubmit.cancel(), [debouncedSubmit]);

  return (
    <Flex className="flex-col">
      <Flex className="flex-col gap-1 py-4">
        <Flex className="flex-row items-center gap-1">
          <RsvpTitle>{t('rsvp.main.deadline.title')}</RsvpTitle>
          <Text variant="typo-sm">({t('rsvp.main.deadline.optional')})</Text>
        </Flex>
        <RsvpText>{t('rsvp.main.deadline.text')}</RsvpText>
      </Flex>
      <Controller
        control={control}
        name="deadline"
        render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
          <DateInputNative
            placeholder={t('rsvp.main.date.placeholder')}
            value={value}
            onChange={onChange}
            onBlur={() => {
              onBlur();
              debouncedSubmit();
            }}
            onFocus={() => triggerPreviewSync('overview')}
            showClearBtn
            onClear={() => {
              onChange('');
              debouncedSubmit();
            }}
            name={name}
            min={new Date().toISOString().split('T')[0]}
            containerStyle={{ width: '100%' }}
            error={error?.message}
          />
        )}
      />
    </Flex>
  );
};
